import type { Namespace } from '../types';

// DE drafted — queued for native review in the task file's "DE review queue".
// Codes are invariant identifiers; the console ships functional-but-empty
// (VALUES come from the walkthroughs).
export const masterData = {
  // R-204 moved crops + varieties to the Cultivation group, so this console now
  // covers only the reference tables nobody manages per crop. R-210 then folded
  // those two screens into the Cultivation catalogue tree, which owns their
  // wording — hence no `tabCrops`/`tabVarieties` here any more.
  title: { de: 'Stammdaten', en: 'Master Data' },
  desc: {
    de: 'Zentrale Referenzdaten für alle Module — Klimazonen, Einheiten und Zonen. Kulturen und Sorten liegen unter Kultivierung.',
    en: 'Central reference data shared by all modules — climate zones, units and zones. Crops and varieties live under Cultivation.',
  },

  tabClimate: { de: 'Klimazonen', en: 'Climate zones' },
  tabUnits: { de: 'Einheiten', en: 'Units' },
  tabZones: { de: 'Zonen', en: 'Zones' },

  colCode: { de: 'Code', en: 'Code' },
  colNameDe: { de: 'Name (DE)', en: 'Name (DE)' },
  colNameEn: { de: 'Name (EN)', en: 'Name (EN)' },
  colName: { de: 'Name', en: 'Name' },
  colCrop: { de: 'Kultur', en: 'Crop' },
  colSupplier: { de: 'Saatgut-Lieferant', en: 'Seed supplier' },
  colDesc: { de: 'Beschreibung', en: 'Description' },
  colKind: { de: 'Art', en: 'Kind' },
  colModel: { de: 'Anbaumodell', en: 'Cultivation model' },
  colCategory: { de: 'Kategorie', en: 'Category' },
  colLatinName: { de: 'Botanischer Name', en: 'Botanical name' },
  colActive: { de: 'Aktiv', en: 'Active' },
  colSite: { de: 'Standort', en: 'Site' },

  colActions: { de: 'Aktionen', en: 'Actions' },
  filterKind: { de: 'Alle Arten', en: 'All kinds' },
  filterSite: { de: 'Alle Standorte', en: 'All sites' },
  noMatch: { de: 'Keine Einträge passen zu diesen Filtern', en: 'No entries match these filters' },
  add: { de: 'Hinzufügen', en: 'Add' },
  // The primary action names what it adds — it follows the active tab.
  addClimate: { de: 'Klimazone hinzufügen', en: 'Add Climate Zone' },
  addUnit: { de: 'Einheit hinzufügen', en: 'Add Unit' },
  addZone: { de: 'Zone hinzufügen', en: 'Add Zone' },
  newClimate: { de: 'Neue Klimazone', en: 'New Climate Zone' },
  newUnit: { de: 'Neue Einheit', en: 'New Unit' },
  newZone: { de: 'Neue Zone', en: 'New Zone' },
  editClimate: { de: 'Klimazone bearbeiten', en: 'Edit climate zone' },
  editUnit: { de: 'Einheit bearbeiten', en: 'Edit unit' },
  editZone: { de: 'Zone bearbeiten', en: 'Edit zone' },
  phClimateCode: { de: 'z. B. TEMPERATE', en: 'e.g. TEMPERATE' },
  phClimateName: { de: 'z. B. Gemäßigt', en: 'e.g. Temperate' },
  phClimateDesc: {
    de: 'Wofür diese Klimazone steht (optional)',
    en: 'What this climate zone stands for (optional)',
  },
  phUnitCode: { de: 'z. B. kg', en: 'e.g. kg' },
  phUnitName: { de: 'z. B. Kilogramm', en: 'e.g. Kilogram' },
  phZoneCode: { de: 'z. B. GR-A', en: 'e.g. GR-A' },
  phZoneName: { de: 'z. B. Anbauraum A', en: 'e.g. Grow room A' },
  phKind: { de: 'Art wählen', en: 'Select a kind' },
  phSite: { de: 'Standort wählen', en: 'Select a site' },
  newEntry: { de: 'Neuer Eintrag', en: 'New entry' },
  editEntry: { de: 'Eintrag bearbeiten', en: 'Edit entry' },
  inactive: { de: 'inaktiv', en: 'inactive' },

  kindMass: { de: 'Masse', en: 'Mass' },
  kindVolume: { de: 'Volumen', en: 'Volume' },
  kindCount: { de: 'Anzahl', en: 'Count' },
  kindTime: { de: 'Zeit', en: 'Time' },
  kindTemperature: { de: 'Temperatur', en: 'Temperature' },
  kindArea: { de: 'Fläche', en: 'Area' },
  kindOther: { de: 'Sonstiges', en: 'Other' },
  zoneKindCultivation: { de: 'Anbau', en: 'Cultivation' },
  zoneKindPropagation: { de: 'Anzucht', en: 'Propagation' },
  zoneKindTechnical: { de: 'Technik', en: 'Technical' },
  zoneKindStorage: { de: 'Lager', en: 'Storage' },
  zoneKindProcessing: { de: 'Verarbeitung', en: 'Processing' },

  deleteTitle: { de: '{code} löschen?', en: 'Delete {code}?' },
  deleteMsg: {
    de: 'Referenzdaten, die bereits verwendet werden, können nicht gelöscht werden.',
    en: 'Reference data that is already in use cannot be deleted.',
  },
  toastSaved: { de: '{code} gespeichert', en: '{code} saved' },
  toastDeleted: { de: '{code} gelöscht', en: '{code} deleted' },

  loadError: { de: 'Stammdaten konnten nicht geladen werden', en: "Couldn't load master data" },
  emptyTitle: { de: 'Noch keine Einträge', en: 'No entries yet' },
  emptyMsg: {
    de: 'Die Werte kommen aus den Workshops — legen Sie den ersten Eintrag an.',
    en: 'Values come from the walkthrough workshops — create the first entry.',
  },
} as const satisfies Namespace;
